/**
 * sw-concierge-drawer — site-wide concierge drawer (live chat, phone, email,
 * showroom, hours). Chat docking and the open/closed status live in
 * sw-concierge-channels.js.
 *
 * Opens from [data-concierge-drawer-trigger] / .trigger-concierge-drawer.
 *
 * Public API: window.SwConcierge = { open(), close(), toggle() }
 * Events: document → 'sw:concierge:open', 'sw:concierge:close'
 */
(function () {
  function activateSideDrawer(element, activeClass = 'active') {
    if (typeof window.activateSideDrawer === 'function') {
      window.activateSideDrawer(element, activeClass);
      return;
    }
    element.classList.remove(activeClass);
    void element.offsetWidth;
    requestAnimationFrame(() => element.classList.add(activeClass));
  }

  function lockScroll() {
    (window.lockBodyScroll || (() => document.body.classList.add('overflow-hidden')))('concierge-drawer');
  }

  function unlockScroll() {
    (window.unlockBodyScroll || (() => document.body.classList.remove('overflow-hidden')))('concierge-drawer');
  }

  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
  }

  class ConciergeDrawer extends HTMLElement {
    static triggerSelector = '.trigger-concierge-drawer, [data-concierge-drawer-trigger]';

    constructor() {
      super();

      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      this.querySelector('#ConciergeDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    }

    connectedCallback() {
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);
      this.exposeApi();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      if (this._scrollWatch) {
        this._scrollWatch.disconnect();
        this._scrollWatch = null;
      }
    }

    get isOpen() {
      return this.classList.contains('active');
    }

    onDocumentClick(event) {
      const trigger = event.target.closest(ConciergeDrawer.triggerSelector);
      if (!trigger || this.contains(trigger)) return;

      event.preventDefault();
      this.open(trigger);
    }

    onDocumentKeydown(event) {
      if (event.code.toUpperCase() !== 'SPACE') return;

      const trigger = event.target.closest(ConciergeDrawer.triggerSelector);
      if (!trigger || this.contains(trigger)) return;

      event.preventDefault();
      this.open(trigger);
    }

    open(triggeredBy) {
      const wasOpen = this.isOpen;
      if (triggeredBy && triggeredBy.nodeType === 1) this.setActiveElement(triggeredBy);
      else if (!wasOpen) this.setActiveElement(document.activeElement);
      this._scrollY = window.scrollY || window.pageYOffset || 0;
      this.classList.remove('is-closed');

      // Only one side drawer may be open at a time.
      document.querySelectorAll('cart-drawer.active, concierge-drawer.active').forEach((drawer) => {
        if (drawer !== this && typeof drawer.close === 'function') drawer.close();
      });

      activateSideDrawer(this);
      this.watchPodiumScrollLock();
      if (window.SwPodium && typeof window.SwPodium.load === 'function') {
        window.SwPodium.load();
      }

      const armTrapFocus = () => {
        if (typeof trapFocus !== 'function') return;
        trapFocus(this.querySelector('.drawer__inner'), this.querySelector('.drawer__close'));
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        // Reduced motion removes the slide transition, so transitionend never
        // fires; trap focus once the drawer is active (next frame after rAF).
        requestAnimationFrame(() => requestAnimationFrame(armTrapFocus));
      } else {
        this.addEventListener('transitionend', armTrapFocus, { once: true });
      }

      lockScroll();
      if (!wasOpen) dispatch('sw:concierge:open', { trigger: this.activeElement });
    }

    close() {
      const wasOpen = this.isOpen;
      this.classList.remove('active', 'animate');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
      unlockScroll();
      this.releaseDocumentScroll({ restore: true });

      const token = (this._closeToken = (this._closeToken || 0) + 1);
      window.setTimeout(() => {
        if (this._closeToken !== token || this.classList.contains('active')) return;
        this.classList.add('is-closed');
      }, 400);

      if (wasOpen) dispatch('sw:concierge:close', {});
    }

    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    }

    /* Podium's mobile widget sets position:fixed on the document when chat
       opens, and it can put that lock back after the drawer has closed. */
    watchPodiumScrollLock() {
      if (this._scrollWatch) return;
      const scrub = () => {
        if (this.classList.contains('active')) return;
        const root = document.documentElement;
        const body = document.body;
        const inlineFixed = root.style.position === 'fixed' || body.style.position === 'fixed';
        const podiumLocked =
          inlineFixed ||
          root.classList.contains('podium-mobile-widget-active') ||
          body.classList.contains('podium-mobile-widget-active');
        if (!podiumLocked) return;
        this.releaseDocumentScroll({ restore: inlineFixed });
      };
      this._scrollWatch = new MutationObserver(scrub);
      this._scrollWatch.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
      this._scrollWatch.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }

    releaseDocumentScroll({ restore = false } = {}) {
      const root = document.documentElement;
      const body = document.body;
      const lockedTop = body.style.top || root.style.top;
      const wasFixed =
        body.style.position === 'fixed' ||
        root.style.position === 'fixed' ||
        root.classList.contains('podium-mobile-widget-active') ||
        body.classList.contains('podium-mobile-widget-active');

      root.classList.remove('podium-mobile-widget-active', 'podium-chat-docked');
      body.classList.remove('podium-mobile-widget-active');
      ['position', 'top', 'left', 'right', 'width', 'height', 'overflow'].forEach((prop) => {
        root.style.removeProperty(prop);
      });

      const anotherOpen = document.querySelector('cart-drawer.active');
      if (anotherOpen) return;

      if (!this.classList.contains('active')) body.classList.remove('overflow-hidden');
      ['position', 'top', 'left', 'right', 'width', 'height', 'overflow'].forEach((prop) => {
        body.style.removeProperty(prop);
      });

      if (!restore && !wasFixed) return;
      const fromLock = lockedTop ? Math.abs(parseInt(lockedTop, 10)) : NaN;
      const y = Number.isNaN(fromLock) ? this._scrollY || 0 : fromLock;
      window.scrollTo(0, y);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    exposeApi() {
      window.SwConcierge = {
        open: () => this.open(),
        close: () => this.close(),
        toggle: () => this.toggle(),
        element: this,
      };
    }
  }

  if (!customElements.get('concierge-drawer')) {
    customElements.define('concierge-drawer', ConciergeDrawer);
  }
})();
